import { searchProvider, type ProviderSummary, type VideoSummary } from "./api.ts";
import { categoryTitle } from "./categories.ts";
import { posterCard, skeletonGrid } from "./components.ts";
import { appendChildren, el } from "./dom.ts";
import { icons } from "./icons.ts";
import type { AppContext } from "./views.ts";
import {
  beginSearch,
  findSearch,
  forgetActiveSearch,
  lastProviderSearch,
  lastSearch,
  markSearchActive,
  MIN_QUERY,
  readLayout,
  refreshSearch,
  rememberProviderSearch,
  retryFailures,
  setProviderSearchScroll,
  setRunExpanded,
  setRunScroll,
  stopSearch,
  subscribe,
  writeLayout,
  type ProviderSearchEntry,
  type SearchLayout,
  type SearchRun,
} from "./search-store.ts";
import {
  clearRecentQueries,
  recentQueries,
  recordQuery,
  searchCategories,
  searchCategoryIds,
  setSearchCategories,
} from "./store.ts";
import { categoriesOf, visibleProviders } from "./visibility.ts";

/** Debounce for the live search field, in milliseconds. */
const DEBOUNCE_MS = 400;
/** Items requested from one provider. */
const PAGE_SIZE = 36;
/** Hard ceiling on preview posters; a broad query can otherwise mount thousands. */
const MAX_RENDERED = 320;
/** Posters shown per provider before its "show all" control. */
const GROUP_PREVIEW = 18;
/** Category label for a provider category id. */

/** Wrap every case-insensitive occurrence of `query` inside a title node. */
function highlightMatches(titleNode: HTMLElement, query: string): void {
  const term = query.trim();
  if (term.length < MIN_QUERY) return;
  const text = titleNode.textContent ?? "";
  const haystack = text.toLowerCase();
  const needle = term.toLowerCase();
  if (!haystack.includes(needle)) return;

  const parts: (string | HTMLElement)[] = [];
  let cursor = 0;
  for (;;) {
    const index = haystack.indexOf(needle, cursor);
    if (index < 0) break;
    if (index > cursor) parts.push(text.slice(cursor, index));
    parts.push(el("mark", { class: "mark", text: text.slice(index, index + term.length) }));
    cursor = index + term.length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));

  titleNode.replaceChildren();
  appendChildren(titleNode, ...parts);
}

/** Poster card whose title carries the highlighted query. */
function searchPoster(
  video: VideoSummary,
  providerLabel: string,
  query: string,
  onOpen: (video: VideoSummary) => void,
): HTMLElement {
  const card = posterCard(video, { providerLabel, onOpen });
  const title = card.querySelector<HTMLElement>(".poster-title");
  if (title) highlightMatches(title, query);
  return card;
}

/** Providers that expose a search path, preferred ones first. */
function searchableSites(context: AppContext): ProviderSummary[] {
  const preferred = new Set(context.preferred);
  return visibleProviders(context.providers)
    .filter((provider) => provider.capabilities.search && provider.state !== "offline")
    .sort(
      (left, right) =>
        Number(preferred.has(right.id)) - Number(preferred.has(left.id)) ||
        left.name.localeCompare(right.name),
    );
}


interface Suggestion {
  id: string;
  kind: "search" | "recent" | "site" | "category" | "browse";
  label: string;
  detail?: string;
  value?: string;
}

/**
 * Combobox over the search field, built to the WAI-ARIA APG pattern: the input
 * keeps DOM focus, `aria-activedescendant` moves the selection, Enter accepts,
 * Escape dismisses. The popup is what makes the field legible to someone who
 * has never used it — it always offers the plain "search for this" action first.
 */
function attachCombobox(options: {
  input: HTMLInputElement;
  form: HTMLFormElement;
  listId: string;
  getItems: (query: string) => Suggestion[];
  onPick: (item: Suggestion) => void;
  onSubmit: () => void;
}): { close: () => void; refresh: () => void } {
  const list = el("ul", {
    class: "search-suggestions",
    attrs: { id: options.listId, role: "listbox", "aria-label": "Search suggestions", hidden: true },
  });
  options.form.append(list);

  let items: Suggestion[] = [];
  let active = -1;

  const paint = (): void => {
    list.replaceChildren();
    for (const [index, item] of items.entries()) {
      const option = el("li", {
        class: "suggestion",
        attrs: {
          id: `${options.listId}-${item.id}`,
          role: "option",
          "aria-selected": String(index === active),
          "data-kind": item.kind,
        },
      });
      option.append(
        el("span", { class: "suggestion-icon", attrs: { "aria-hidden": "true" }, html: icons.search }),
        el(
          "span",
          { class: "suggestion-text" },
          el("span", { class: "suggestion-label", text: item.label }),
          item.detail ? el("span", { class: "suggestion-detail", text: item.detail }) : null,
        ),
      );
      option.addEventListener("mousedown", (event) => {
        event.preventDefault();
        pick(index);
      });
      list.append(option);
    }
    const open = items.length > 0;
    list.hidden = !open;
    options.input.setAttribute("aria-expanded", String(open));
    if (open && active >= 0 && items[active]) {
      options.input.setAttribute("aria-activedescendant", `${options.listId}-${items[active].id}`);
    } else {
      options.input.removeAttribute("aria-activedescendant");
    }
    if (!open) active = -1;
  };

  const pick = (index: number): void => {
    const item = items[index];
    if (!item) return;
    close();
    options.onPick(item);
  };

  const close = (): void => {
    items = [];
    active = -1;
    paint();
  };

  const refresh = (): void => {
    const query = options.input.value.trim();
    items = options.getItems(query);
    active = items.length > 0 ? 0 : -1;
    paint();
  };

  options.input.setAttribute("role", "combobox");
  options.input.setAttribute("aria-autocomplete", "list");
  options.input.setAttribute("aria-controls", options.listId);
  options.input.setAttribute("aria-expanded", "false");

  options.input.addEventListener("focus", () => refresh());
  options.input.addEventListener("blur", () => window.setTimeout(close, 120));
  options.input.addEventListener("input", () => refresh());
  options.input.addEventListener("keydown", (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (items.length === 0) {
        refresh();
        if (items.length === 0) return;
      }
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      active = (active + step + items.length) % items.length;
      paint();
      return;
    }
    if (event.key === "Escape" && items.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key === "Enter" && items.length > 0 && active >= 0) {
      event.preventDefault();
      pick(active);
    }
  });

  return { close, refresh };
}

function searchField(options: {
  placeholder: string;
  label: string;
  large?: boolean;
  describedBy?: string;
  controls?: string;
  onInput: () => void;
  onSubmit: () => void;
  /** When set, the field carries its own submit button. */
  submitLabel?: string;
}): { form: HTMLFormElement; input: HTMLInputElement; clear: HTMLButtonElement } {
  const input = el("input", {
    class: options.large ? "input input--lg" : "input",
    attrs: {
      type: "search",
      name: "q",
      placeholder: options.placeholder,
      "aria-label": options.label,
      "aria-describedby": options.describedBy,
      "aria-controls": options.controls,
      autocomplete: "off",
      spellcheck: "false",
      enterkeyhint: "search",
    },
  });
  const clear = el("button", {
    class: "field-clear",
    attrs: { type: "button", "aria-label": "Clear search" },
    html: icons.close,
  });
  const syncClear = (): void => {
    clear.hidden = input.value.length === 0;
  };

  const form = el(
    "form",
    { class: "field field--search", attrs: { role: "search", "aria-label": options.label } },
    el("span", { class: "field-icon", html: icons.search }),
    input,
    clear,
  );
  if (options.submitLabel) {
    form.append(
      el("button", {
        class: "btn btn-primary search-submit",
        attrs: { type: "submit" },
        text: options.submitLabel,
      }),
    );
  }

  input.addEventListener("input", () => {
    syncClear();
    options.onInput();
  });
  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && input.value.length > 0) {
      event.preventDefault();
      input.value = "";
      syncClear();
      options.onSubmit();
      return;
    }
    if (event.key === "Escape") input.blur();
  });
  input.addEventListener("keydown", (event) => {
    if (event.key !== "ArrowDown") return;
    const first = document.querySelector<HTMLElement>("#search-results .poster");
    if (!first) return;
    event.preventDefault();
    first.focus();
  });
  clear.addEventListener("click", () => {
    input.value = "";
    syncClear();
    options.onSubmit();
    input.focus();
  });
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    options.onSubmit();
  });

  syncClear();
  return { form, input, clear };
}

/** Announcements are throttled through one polite region so batches do not chatter. */
function liveAnnouncer(): { node: HTMLElement; say: (text: string) => void; stop: () => void } {
  const node = el("p", {
    class: "visually-hidden",
    attrs: { role: "status", "aria-live": "polite", "aria-atomic": "true" },
  });
  let timer = 0;
  return {
    node,
    say: (text: string) => {
      window.clearTimeout(timer);
      node.textContent = "";
      timer = window.setTimeout(() => {
        node.textContent = text;
      }, 80);
    },
    stop: () => window.clearTimeout(timer),
  };
}

function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

interface CategoryChoice {
  id: string;
  title: string;
  count: number;
}

/**
 * Global search: one field, a scope that can be a set of category groups or
 * individual sites, and results grouped per provider. A search is a shared run
 * (see search-store) rather than a page: opening a title and coming back paints
 * the same answer, the same scope and the same scroll position.
 */
export function globalSearchView(
  context: AppContext,
  initialQuery = "",
  initialSites: string[] = [],
): HTMLElement {
  const root = el("div", { class: "view search-page" });
  const allSites = searchableSites(context);
  const categoryIds = categoriesOf(allSites);
  const categoryChoices: CategoryChoice[] = categoryIds.map((id) => ({
    id,
    title: categoryTitle(id),
    count: allSites.filter((provider) => provider.category === id).length,
  }));
  const names = new Map(context.providers.map((provider) => [provider.id, provider.name]));

  const validSites = new Set(allSites.map((provider) => provider.id));
  const selectedSites = new Set(
    initialSites.filter((id) => validSites.has(id)).slice(0, allSites.length),
  );

  const activeCategories = (): Set<string> => new Set(searchCategoryIds(categoryIds));
  const inScope = (): ProviderSummary[] => {
    const active = activeCategories();
    return allSites.filter((provider) => active.has(provider.category));
  };
  const effectiveSites = (): ProviderSummary[] => {
    const scope = inScope();
    if (selectedSites.size === 0) return scope;
    return scope.filter((provider) => selectedSites.has(provider.id));
  };

  const announcer = liveAnnouncer();
  const results = el("div", {
    class: "search-results",
    attrs: {
      id: "search-results",
      role: "region",
      "aria-label": "Search results",
      tabindex: "-1",
    },
  });
  const skeleton = el("div", { attrs: { "aria-hidden": "true" } });
  const groupsBox = el("div", { class: "search-groups" });
  const note = el("div", { class: "search-note-slot" });
  const failuresBox = el("div", { class: "search-failures-slot" });
  const emptyBox = el("div", { class: "search-empty" });
  results.append(skeleton, failuresBox, note, groupsBox, emptyBox);

  const stateTitle = el("p", { class: "search-state-title" });
  const stateSub = el("p", { class: "search-state-sub" });
  const stateActions = el("div", { class: "search-state-actions" });
  const stateBlock = el(
    "div",
    { class: "search-state", attrs: { "data-state": "idle", "aria-live": "polite" } },
    el("span", { class: "search-state-dot", attrs: { "aria-hidden": "true" } }),
    el("div", { class: "search-state-copy" }, stateTitle, stateSub),
    stateActions,
  );
  const status = stateTitle;
  const progress = el("progress", {
    class: "search-progress",
    attrs: { "aria-hidden": "true", hidden: true },
  });
  const scopePills = el("div", { class: "search-scope-pills" });
  const scopeCount = el("span", { class: "scope-count" });
  const scopeText = el("span", { attrs: { id: "search-scope-text" } });
  const changeScope = el("button", {
    class: "ghost-link",
    attrs: { type: "button" },
    text: "Change sites",
  });

  const layoutButtons = new Map<SearchLayout, HTMLButtonElement>();
  const layoutGroup = el("div", {
    class: "tag-row search-layout",
    attrs: { role: "group", "aria-label": "Result layout" },
  });
  for (const [value, label] of [
    ["grouped", "Grouped by site"],
    ["flat", "One grid"],
  ] as const) {
    const button = el("button", {
      class: "tag",
      attrs: { type: "button", "aria-pressed": String(readLayout() === value) },
      text: label,
    });
    button.addEventListener("click", () => {
      writeLayout(value);
      for (const [key, chip] of layoutButtons) {
        chip.setAttribute("aria-pressed", String(key === value));
      }
      layout = value;
      if (activeRun) renderAll(activeRun);
      announcer.say(value === "grouped" ? "Results grouped by site." : "One grid of all results.");
    });
    layoutButtons.set(value, button);
    layoutGroup.append(button);
  }

  const actionSlot = el("div", { class: "search-actions" });
  const kept = el("p", { class: "search-kept", text: "Kept from a previous search" });
  kept.hidden = true;
  const toolbar = el("div", { class: "search-toolbar" }, layoutGroup, actionSlot);
  toolbar.hidden = true;

  let layout: SearchLayout = readLayout();
  let activeRun: SearchRun | null = null;
  let unsubscribe = (): void => {};
  let currentQuery = "";
  let restored = false;
  let scrollRestored = false;
  let previewBudget = MAX_RENDERED;
  let flatRendered = 0;
  let flatList: HTMLUListElement | null = null;
  let debounce = 0;
  let scrollRaf = 0;
  let actionsMode = "";
  let failuresKey = "";
  const painted = new Set<string>();
  const sections = new Map<string, HTMLElement>();
  const renderedCounts = new Map<string, number>();
  const resultCounts = new Map<string, number>();

  // ---- scope controls -------------------------------------------------

  const categoryRow = el("div", {
    class: "tag-row",
    attrs: { role: "group", "aria-label": "Category groups" },
  });
  const siteRow = el("div", {
    class: "tag-row scope-sites",
    attrs: { role: "group", "aria-label": "Sites" },
  });
  const siteChips = new Map<string, HTMLButtonElement>();
  const categoryChips = new Map<string, HTMLButtonElement>();

  const allCategories = el("button", {
    class: "tag",
    attrs: { type: "button" },
    text: "All categories",
  });
  allCategories.addEventListener("click", () => {
    setSearchCategories([]);
    scopeChanged();
  });
  categoryRow.append(allCategories);

  for (const choice of categoryChoices) {
    const chip = el("button", {
      class: "tag",
      attrs: {
        type: "button",
        "aria-pressed": "false",
        "aria-label": `Search every site in ${choice.title}`,
        title: `Search every site in ${choice.title}`,
      },
    });
    chip.append(
      el("span", { text: choice.title }),
      el("span", { class: "tag-count", text: String(choice.count) }),
    );
    chip.addEventListener("click", () => {
      const raw = searchCategories();
      let next: string[];
      if (raw.length === 0) {
        // Everything is on. One click means "search this group", not
        // "everything except this group".
        next = [choice.id];
      } else {
        const chosen = new Set([...raw].filter((id) => categoryIds.includes(id)));
        if (chosen.has(choice.id)) chosen.delete(choice.id);
        else chosen.add(choice.id);
        next = [...chosen];
        // An empty selection and a full selection both mean "every category".
        if (next.length === 0 || next.length === categoryIds.length) next = [];
      }
      setSearchCategories(next);
      scopeChanged();
    });
    categoryChips.set(choice.id, chip);
    categoryRow.append(chip);
  }

  const siteFilter = el("input", {
    class: "input",
    attrs: {
      type: "search",
      placeholder: "Filter sites",
      "aria-label": "Filter sites",
      autocomplete: "off",
      spellcheck: "false",
    },
  });
  const siteFilterField = el(
    "div",
    { class: "field field--filter scope-filter" },
    el("span", { class: "field-icon", html: icons.search }),
    siteFilter,
  );
  let siteListOpen = false;
  const siteToggle = el("button", {
    class: "ghost-link scope-toggle",
    attrs: { type: "button", "aria-expanded": "false" },
    text: "Choose specific sites",
  });
  siteToggle.addEventListener("click", () => {
    siteListOpen = !siteListOpen;
    siteToggle.setAttribute("aria-expanded", String(siteListOpen));
    siteToggle.textContent = siteListOpen ? "Hide the site list" : "Choose specific sites";
    paintSiteChips();
  });

  const siteTools = el("div", { class: "scope-tools" }, siteFilterField, siteToggle);
  siteFilter.addEventListener("input", () => paintSiteChips());

  const clearSites = el("button", {
    class: "ghost-link",
    attrs: { type: "button" },
    text: "Clear sites",
  });
  clearSites.hidden = true;
  clearSites.addEventListener("click", () => {
    selectedSites.clear();
    scopeChanged();
  });

  const scopePanel = el(
    "details",
    { class: "scope search-scope" },
    el(
      "summary",
      { class: "scope-summary" },
      el("span", { text: "Search in" }),
      scopeCount,
      el("span", { class: "scope-chevron", html: icons.chevron }),
    ),
    el(
      "div",
      { class: "scope-body" },
      el(
        "div",
        { class: "scope-group" },
        el(
          "div",
          { class: "scope-group-head" },
          el("span", { class: "scope-label", text: "Categories" }),
          el("span", { class: "scope-hint", text: "Leave one on to search only that group" }),
        ),
        categoryRow,
      ),
      el(
        "div",
        { class: "scope-group" },
        el(
          "div",
          { class: "scope-group-head" },
          el("span", { class: "scope-label", text: "Sites" }),
          el("span", { class: "scope-hint", text: "All of them, unless you pick a few" }),
          clearSites,
        ),
        siteTools,
        siteRow,
      ),
    ),
  );

  changeScope.addEventListener("click", () => {
    scopePanel.open = true;
    scopePanel.scrollIntoView({ block: "nearest" });
    scopePanel.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true });
  });

  const scopeLine = el(
    "p",
    { class: "search-scope-line", attrs: { id: "search-scope-line" } },
    scopeText,
    changeScope,
  );

  const paintSiteChips = (): void => {
    const filter = siteFilter.value.trim().toLowerCase();
    const picking = siteListOpen || filter.length > 0;
    if (!picking && selectedSites.size === 0) {
      siteChips.clear();
      siteRow.replaceChildren(
        el("p", {
          class: "scope-empty",
          text: "Every site in the groups above is searched. Open the list to pick a few.",
        }),
      );
      return;
    }
    const scope = inScope().filter(
      (provider) => filter.length === 0 || provider.name.toLowerCase().includes(filter),
    );
    siteChips.clear();
    if (scope.length === 0) {
      siteRow.replaceChildren(
        el("p", { class: "scope-empty", text: "No site in this scope matches that filter." }),
      );
      return;
    }
    const nodes = scope.map((provider) => {
      const chip = el("button", {
        class: "tag",
        attrs: {
          type: "button",
          "aria-pressed": String(selectedSites.has(provider.id)),
          title: provider.name,
        },
        text: provider.name,
      });
      chip.addEventListener("click", () => {
        if (selectedSites.has(provider.id)) selectedSites.delete(provider.id);
        else selectedSites.add(provider.id);
        scopeChanged();
        siteChips.get(provider.id)?.focus({ preventScroll: true });
      });
      siteChips.set(provider.id, chip);
      return chip;
    });
    siteRow.replaceChildren(...nodes);
    syncSiteChips();
  };

  const syncSiteChips = (): void => {
    resultCounts.clear();
    const answered = new Set(activeRun?.answered ?? []);
    const answeredGroups = new Set<string>();
    for (const group of activeRun?.groups ?? []) {
      resultCounts.set(group.provider, group.items.length);
      answeredGroups.add(group.provider);
    }
    for (const [id, chip] of siteChips) {
      chip.setAttribute("aria-pressed", String(selectedSites.has(id)));
      const name = names.get(id) ?? chip.title ?? id;
      if (!resultCounts.has(id) && !answered.has(id)) {
        chip.replaceChildren(document.createTextNode(name));
        chip.setAttribute("aria-label", name);
        continue;
      }
      const count = resultCounts.get(id) ?? 0;
      chip.replaceChildren(
        document.createTextNode(name),
        el("span", { class: "tag-count", text: String(count) }),
      );
      chip.setAttribute(
        "aria-label",
        `${name}, ${plural(count, "result")}${answered.has(id) && !answeredGroups.has(id) ? ", no results" : ""}`,
      );
    }
  };

  const paintScope = (): void => {
    const explicit = new Set(searchCategories().filter((id) => categoryIds.includes(id)));
    for (const [id, chip] of categoryChips) {
      chip.setAttribute("aria-pressed", String(explicit.has(id)));
    }
    allCategories.setAttribute("aria-pressed", String(explicit.size === 0));
    clearSites.hidden = selectedSites.size === 0;
    const effective = effectiveSites();
    scopeCount.textContent = `${effective.length} of ${allSites.length}`;
    const active = activeCategories();
    const groups = categoryIds.filter((id) => active.has(id)).map((id) => categoryTitle(id));
    const narrowed = groups.length !== categoryIds.length;
    const parts = [`Searching ${effective.length} of ${allSites.length} sites`];
    if (narrowed && groups.length > 0) parts.push(groups.join(", "));
    if (selectedSites.size > 0) parts.push(`${plural(selectedSites.size, "picked site")}`);
    scopeText.textContent = parts.join(" · ");

    // The same scope as pills, so the state is visible without reading a
    // sentence: one pill per narrowing, each removable in place.
    scopePills.replaceChildren();
    if (narrowed) {
      for (const id of categoryIds.filter((entry) => active.has(entry))) {
        const pill = el("button", {
          class: "tag tag--removable",
          attrs: { type: "button", "aria-label": `Stop searching only ${categoryTitle(id)}`, "aria-pressed": "true" },
          text: categoryTitle(id),
        });
        pill.addEventListener("click", () => {
          setSearchCategories([]);
          scopeChanged();
        });
        scopePills.append(pill);
      }
    }
    if (selectedSites.size > 0) {
      for (const id of selectedSites) {
        const provider = allSites.find((entry) => entry.id === id);
        const pill = el("button", {
          class: "tag tag--removable",
          attrs: { type: "button", "aria-label": `Stop searching only ${provider?.name ?? id}`, "aria-pressed": "true" },
          text: provider?.name ?? id,
        });
        pill.addEventListener("click", () => {
          selectedSites.delete(id);
          scopeChanged();
        });
        scopePills.append(pill);
      }
    }
    scopePills.hidden = scopePills.childElementCount === 0;
  };

  const pruneSites = (): void => {
    const scope = new Set(inScope().map((provider) => provider.id));
    for (const id of [...selectedSites]) if (!scope.has(id)) selectedSites.delete(id);
  };

  const scopeChanged = (): void => {
    pruneSites();
    paintScope();
    paintSiteChips();
    syncUrl(currentQuery);
    if (currentQuery.length >= MIN_QUERY) {
      startSearchFor(currentQuery);
      announcer.say(`${plural(effectiveSites().length, "site")} in scope.`);
    }
  };

  // ---- results rendering ----------------------------------------------

  const resetResults = (): void => {
    groupsBox.replaceChildren();
    note.replaceChildren();
    failuresBox.replaceChildren();
    emptyBox.replaceChildren();
    skeleton.replaceChildren();
    painted.clear();
    sections.clear();
    renderedCounts.clear();
    failuresKey = "";
    flatList = null;
    flatRendered = 0;
    previewBudget = MAX_RENDERED;
    results.removeAttribute("aria-busy");
  };

  const ordered = (run: SearchRun): SearchRun["groups"] =>
    [...run.groups].sort(
      (left, right) => right.items.length - left.items.length || left.name.localeCompare(right.name),
    );

  const openResult = (video: VideoSummary): void => {
    if (activeRun) setRunScroll(activeRun, window.scrollY);
    context.navigate(`#/watch/${encodeURIComponent(video.provider)}/${encodeURIComponent(video.id)}`);
  };

  const renderGroupSection = (section: HTMLElement, group: SearchRun["groups"][number]): void => {
    if (!activeRun) return;
    const isExpanded = activeRun.expanded.includes(group.provider);
    const previous = renderedCounts.get(group.provider) ?? 0;
    let shown = isExpanded ? group.items.length : Math.min(GROUP_PREVIEW, group.items.length);
    if (!isExpanded) shown = Math.min(shown, Math.max(0, previewBudget));
    const delta = shown - previous;
    previewBudget = Math.max(0, previewBudget - delta);
    renderedCounts.set(group.provider, shown);

    const sectionId = `search-group-${group.provider.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
    const head = el(
      "div",
      { class: "section-head" },
      el("h2", { class: "section-title", text: group.name }),
      el(
        "div",
        { class: "section-actions" },
        el("span", { class: "search-group-count", text: plural(group.items.length, "result") }),
      ),
    );

    let toggle: HTMLButtonElement | null = null;
    if (group.items.length > shown || isExpanded) {
      toggle = el("button", {
        class: "ghost-link result-toggle",
        attrs: {
          type: "button",
          "aria-expanded": String(isExpanded),
          "aria-controls": sectionId,
        },
        text: isExpanded ? "Show less" : `Show all ${group.items.length}`,
      });
      toggle.addEventListener("click", () => {
        if (!activeRun) return;
        const nextExpanded = !activeRun.expanded.includes(group.provider);
        setRunExpanded(activeRun, group.provider, nextExpanded);
        renderGroupSection(section, group);
        updateNote(activeRun);
        section.querySelector<HTMLButtonElement>(".result-toggle")?.focus({ preventScroll: true });
      });
      (head.lastElementChild as HTMLElement).append(toggle);
    }

    const grid = el("ul", { class: "grid", attrs: { id: sectionId } });
    for (const item of group.items.slice(0, shown)) {
      grid.append(searchPoster(item, group.name, activeRun.query, openResult));
    }

    section.replaceChildren(head);
    // Keep the controlled list in the DOM even when a budget-starved group
    // shows no posters, so aria-controls always resolves.
    if (grid.childElementCount > 0 || toggle) section.append(grid);
    section.dataset.provider = group.provider;
  };

  const appendGroup = (group: SearchRun["groups"][number]): void => {
    if (painted.has(group.provider)) return;
    painted.add(group.provider);
    const section = el("section", { class: "result-group" });
    sections.set(group.provider, section);
    groupsBox.append(section);
    renderGroupSection(section, group);
  };

  const appendFlat = (group: SearchRun["groups"][number]): void => {
    if (painted.has(group.provider) || !flatList) return;
    painted.add(group.provider);
    const room = Math.max(0, MAX_RENDERED - flatRendered);
    const items = group.items.slice(0, room);
    flatRendered += items.length;
    for (const item of items) {
      flatList.append(searchPoster(item, group.name, activeRun?.query ?? "", openResult));
    }
  };

  const paintSkeleton = (run: SearchRun): void => {
    const pending = run.state === "running" && run.groups.length === 0;
    if (pending) {
      if (skeleton.childElementCount === 0) skeleton.append(skeletonGrid(12, "Searching the catalogue"));
    } else {
      skeleton.replaceChildren();
    }
  };

  const updateNote = (run: SearchRun): void => {
    note.replaceChildren();
    if (run.state === "running") return;
    const rendered = layout === "flat"
      ? flatRendered
      : [...renderedCounts.values()].reduce((sum, count) => sum + count, 0);
    if (rendered < run.total) {
      note.append(
        el("p", {
          class: "empty-body",
          text: `Showing the first ${rendered} of ${run.total} results. Open a site or narrow the search to see the rest.`,
        }),
      );
    }
  };

  const paintFailures = (run: SearchRun): void => {
    const key = run.failures
      .map((failure) => `${failure.provider}:${failure.reason}`)
      .join("|");
    if (key === failuresKey) return;
    failuresKey = key;
    failuresBox.replaceChildren();
    if (run.failures.length === 0) return;
    const list = el(
      "ul",
      {},
      ...run.failures.slice(0, 24).map((failure) =>
        el("li", {}, el("strong", { text: failure.name }), el("span", { text: failure.reason })),
      ),
    );
    const retry = el("button", {
      class: "btn btn-ghost btn-sm",
      attrs: { type: "button" },
      text: `Retry ${plural(run.failures.length, "site")}`,
    });
    retry.addEventListener("click", () => {
      const next = retryFailures(run);
      if (!next) return;
      attachRun(next, false);
      announcer.say(`Retrying ${plural(run.failures.length, "site")}.`);
      results.focus({ preventScroll: true });
    });
    const block = el(
      "details",
      { class: "search-failures" },
      el("summary", { text: `${plural(run.failures.length, "site")} did not answer` }),
      run.failures.length > 24
        ? el("p", { class: "search-failures-more", text: `And ${run.failures.length - 24} more.` })
        : null,
      list,
      retry,
    );
    failuresBox.append(block);
  };

  const paintEmpty = (run: SearchRun): void => {
    emptyBox.replaceChildren();
    if (run.state === "running" || run.total > 0) return;
    if (run.failures.length > 0) {
      const retry = el("button", {
        class: "btn btn-ghost btn-sm",
        attrs: { type: "button" },
        text: "Try again",
      });
      retry.addEventListener("click", () => {
        const next = refreshSearch(run);
        attachRun(next, false);
        results.focus({ preventScroll: true });
      });
      emptyBox.append(
        el(
          "div",
          { class: "empty" },
          el("p", { class: "empty-title", text: "The catalogue could not be reached" }),
          el("p", {
            class: "empty-body",
            text: `${plural(run.failures.length, "site")} failed to answer. That is a connection problem, not an empty result.`,
          }),
          retry,
        ),
      );
      return;
    }

    const actions = el("div", { class: "search-empty-actions" });
    if (selectedSites.size > 0 || searchCategories().length > 0) {
      const clear = el("button", {
        class: "btn btn-ghost btn-sm",
        attrs: { type: "button" },
        text: "Clear filters",
      });
      clear.addEventListener("click", () => {
        selectedSites.clear();
        setSearchCategories([]);
        scopeChanged();
        results.focus({ preventScroll: true });
      });
      actions.append(clear);
    }
    if (input.value.length > 0) {
      const clear = el("button", {
        class: "btn btn-ghost btn-sm",
        attrs: { type: "button" },
        text: "Clear the search",
      });
      clear.addEventListener("click", () => {
        input.value = "";
        inputClear.hidden = true;
        startSearchFor("");
        input.focus();
      });
      actions.append(clear);
    }
    emptyBox.append(
      el(
        "div",
        { class: "empty" },
        el("p", { class: "empty-title", text: `Nothing matched “${run.query}”` }),
        el("p", {
          class: "empty-body",
          text:
            selectedSites.size > 0
              ? "No title in the picked sites matched. Clear the site filter or try a broader term."
              : activeCategories().size < categoryIds.length
                ? "No title in this category group matched. Widen the group or try a broader term."
                : "No site in the catalogue matched. Try fewer words.",
        }),
        actions,
      ),
    );
  };

  const paintStatus = (run: SearchRun): void => {
    const answered = `${run.answered.length} of ${run.ids.length}`;
    stateActions.replaceChildren();
    if (run.state === "running") {
      stateBlock.dataset.state = "running";
      stateTitle.textContent = `Searching ${run.ids.length} ${run.ids.length === 1 ? "site" : "sites"} for “${run.query}”`;
      stateSub.textContent = `${run.answered.length} answered · ${plural(run.total, "result")} so far`;
      progress.max = Math.max(1, run.ids.length);
      progress.value = run.answered.length;
      progress.hidden = false;
      results.setAttribute("aria-busy", "true");
      return;
    }
    progress.hidden = true;
    results.removeAttribute("aria-busy");
    if (run.state === "stopped") {
      stateBlock.dataset.state = "done";
      stateTitle.textContent = `${plural(run.total, "result")} for “${run.query}”`;
      stateSub.textContent = `Stopped after ${run.answered.length} of ${run.ids.length} sites.`;
      return;
    }
    stateBlock.dataset.state = run.total === 0 ? "empty" : "done";
    stateTitle.textContent =
      run.total === 0
        ? `Nothing for “${run.query}”`
        : `${plural(run.total, "result")} for “${run.query}”`;
    const failed = run.failures.length > 0 ? ` · ${plural(run.failures.length, "site")} did not answer` : "";
    stateSub.textContent =
      run.total === 0
        ? `Checked ${run.answered.length} ${run.answered.length === 1 ? "site" : "sites"}. Fewer words usually help.`
        : `From ${plural(run.answered.length, "site")}${failed}`;
  };

  const actionsFor = (run: SearchRun): string =>
    run.state === "running" ? "running" : run.state === "stopped" ? "stopped" : "done";

  const updateActions = (run: SearchRun): void => {
    const mode = actionsFor(run);
    if (mode === actionsMode) return;
    const hadFocus = actionSlot.contains(document.activeElement);
    actionsMode = mode;
    actionSlot.replaceChildren();

    if (run.state === "running") {
      const stop = el("button", {
        class: "btn btn-ghost btn-sm",
        attrs: { type: "button" },
        text: "Stop",
      });
      stop.addEventListener("click", () => {
        stopSearch(run);
        announcer.say(`Search stopped. ${plural(run.total, "result")} so far.`);
      });
      actionSlot.append(stop);
    } else {
      if (run.answered.length < run.ids.length) {
        const resume = el("button", {
          class: "btn btn-ghost btn-sm",
          attrs: { type: "button" },
          text: "Resume",
        });
        resume.addEventListener("click", () => {
          const next = beginSearch({
            query: run.query,
            ids: run.ids,
            sites: run.sites,
            names,
            seed: run,
          });
          attachRun(next, false);
        });
        actionSlot.append(resume);
      }
      const refresh = el("button", {
        class: "btn btn-ghost btn-sm",
        attrs: { type: "button" },
        text: "Refresh",
      });
      refresh.addEventListener("click", () => attachRun(refreshSearch(run), false));
      actionSlot.append(refresh);
    }
    if (hadFocus) actionSlot.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
  };

  const restoreScroll = (run: SearchRun): void => {
    if (!restored || scrollRestored || run.scrollY <= 0) return;
    scrollRestored = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.scrollTo({ top: run.scrollY }));
    });
  };

  const renderAll = (run: SearchRun): void => {
    resetResults();
    actionsMode = "";
    toolbar.hidden = false;
    kept.hidden = !(restored && run.state !== "running");
    if (layout === "flat") {
      flatList = el("ul", { class: "grid", attrs: { "aria-label": "All search results" } });
      groupsBox.append(flatList);
      for (const group of ordered(run)) appendFlat(group);
    } else {
      for (const group of ordered(run)) appendGroup(group);
    }
    paintSkeleton(run);
    paintStatus(run);
    paintFailures(run);
    paintEmpty(run);
    updateNote(run);
    updateActions(run);
    syncSiteChips();
  };

  const incremental = (run: SearchRun): void => {
    kept.hidden = !(restored && run.state !== "running");
    if (layout === "flat") {
      for (const group of ordered(run)) appendFlat(group);
    } else {
      for (const group of ordered(run)) appendGroup(group);
    }
    paintSkeleton(run);
    paintStatus(run);
    paintFailures(run);
    paintEmpty(run);
    updateNote(run);
    updateActions(run);
    syncSiteChips();
  };

  const finalize = (run: SearchRun): void => {
    if (layout === "grouped") {
      const order = ordered(run)
        .map((group) => sections.get(group.provider))
        .filter((section): section is HTMLElement => Boolean(section));
      groupsBox.replaceChildren(...order);
    }
    paintStatus(run);
    paintFailures(run);
    paintEmpty(run);
    updateNote(run);
    updateActions(run);
    syncSiteChips();
    kept.hidden = !(restored && run.state !== "running");
    if (run.total > 0) recordQuery(run.query);
    paintRecent();
    if (run.state === "done") {
      const failures = run.failures.length > 0 ? `. ${plural(run.failures.length, "site")} did not answer.` : "";
      announcer.say(`${plural(run.total, "result")} from ${plural(run.answered.length, "site")}${failures}`);
    }
  };

  const attachRun = (run: SearchRun, fromCache: boolean, restorePosition = false): void => {
    const previousKey = activeRun?.key ?? null;
    unsubscribe();
    markSearchActive(run);
    activeRun = run;
    // The "kept" label waits for a finished answer so it never claims more
    // than is on screen; scroll is restored only when the page is re-entered,
    // not when the reader changes a query in place.
    restored = fromCache;
    scrollRestored = false;
    unsubscribe = subscribe(run, (updated, event) => {
      if (updated !== activeRun) return;
      if (event === "update") return incremental(updated);
      if (event === "done") {
        incremental(updated);
        finalize(updated);
      }
    });
    renderAll(run);
    if (restorePosition) {
      restoreScroll(run);
    } else if (previousKey !== run.key) {
      requestAnimationFrame(() => window.scrollTo({ top: 0 }));
    }
    if (run.state === "running") {
      announcer.say(`Searching ${plural(run.ids.length, "site")} for ${run.query}.`);
    } else if (restored) {
      announcer.say(`Showing your last search for ${run.query}. ${plural(run.total, "result")}.`);
    }
  };

  // ---- query and URL --------------------------------------------------

  const syncUrl = (term: string): void => {
    const params = new URLSearchParams();
    if (term.trim().length > 0) params.set("q", term.trim());
    if (selectedSites.size > 0) params.set("sites", [...selectedSites].join(","));
    const query = params.toString();
    const hash = `#/search${query ? `?${query}` : ""}`;
    if (window.location.hash === hash) return;
    window.history.replaceState(null, "", hash);
  };

  const showIdle = (): void => {
    activeRun = null;
    forgetActiveSearch();
    unsubscribe();
    unsubscribe = () => {};
    resetResults();
    toolbar.hidden = true;
    kept.hidden = true;
    const effective = effectiveSites();
    if (effective.length === 0) {
      status.textContent = "No sites in this scope. Widen the category groups or clear the site filter.";
      emptyBox.append(
        el(
          "div",
          { class: "empty" },
          el("p", { class: "empty-title", text: "Nothing to search" }),
          el("p", { class: "empty-body", text: "Pick at least one category group or site under Search in." }),
        ),
      );
      return;
    }
    status.textContent = `Type at least two characters to search ${plural(effective.length, "site")}.`;
    emptyBox.append(
      el(
        "div",
        { class: "empty" },
        el("p", { class: "empty-title", text: "Search the catalogue" }),
        el("p", {
          class: "empty-body",
          text: `${plural(effective.length, "site")} ready. Results stay put when you open a title, so coming back does not search again.`,
        }),
      ),
    );
  };

  const startSearchFor = (value: string, restorePosition = false): void => {
    hint.textContent = "";
    const term = value.trim();
    currentQuery = term;
    paintRecent();
    if (term.length < MIN_QUERY) {
      syncUrl(term);
      showIdle();
      return;
    }
    const sites = effectiveSites();
    if (sites.length === 0) {
      showIdle();
      return;
    }
    syncUrl(term);
    const ids = sites.map((site) => site.id);
    const cached = findSearch(term, ids);
    const run = cached ?? beginSearch({ query: term, ids, sites: [...selectedSites], names });
    attachRun(run, Boolean(cached), restorePosition && Boolean(cached));
  };

  // ---- field and recent queries ---------------------------------------

  let paintRecent = (): void => {};
  const field = searchField({
    placeholder: "Search every site",
    label: "Search all sites",
    large: true,
    describedBy: "search-scope-text",
    controls: "search-results",
    submitLabel: "Search",
    // Searching 140 sites on every keystroke is what made this annoying: the
    // page never settled. The global field waits for Enter or the button; the
    // per-provider field is the one that searches as you type.
    onInput: () => {
      paintRecent();
      window.clearTimeout(debounce);
      const value = field.input.value.trim();
      syncUrl(value);
      if (value.length < MIN_QUERY) {
        startSearchFor(value);
        hint.textContent =
          value.length === 1 ? "One more character and Enter will search." : "";
        return;
      }
      // The popup states the action; the hint is only for the too-short case.
      hint.textContent = "";
    },
    onSubmit: () => {
      window.clearTimeout(debounce);
      hint.textContent = "";
      startSearchFor(field.input.value);
    },
  });
  const input = field.input;
  const inputClear = field.clear;
  const hint = el("p", { class: "search-hint", attrs: { "aria-live": "polite" } });

  // The popup carries the same three answers every time: search everything, one
  // of your last searches, or jump to a site. Someone who has never used the
  // page can pick the first row and get a result.
  attachCombobox({
    input,
    form: field.form,
    listId: "search-suggestions",
    getItems: (raw) => {
      const query = raw.trim();
      const lower = query.toLowerCase();
      const items: Suggestion[] = [];
      // An untouched field still has something to offer: the biggest shelves in
      // the catalogue. Nothing to type, nothing to guess.
      if (query.length < MIN_QUERY) {
        const ranked = categoryIds
          .map((id) => ({ id, size: allSites.filter((entry) => entry.category === id).length }))
          .sort((left, right) => right.size - left.size)
          .slice(0, 5);
        for (const entry of ranked) {
          items.push({
            id: `cat-${entry.id}`,
            kind: "category",
            label: categoryTitle(entry.id),
            detail: `${entry.size} sites`,
            value: entry.id,
          });
        }
        items.push({ id: "browse", kind: "browse", label: "Every provider, A to Z", detail: "Browse" });
        return items;
      }
      if (query.length >= MIN_QUERY) {
        items.push({
          id: "all",
          kind: "search",
          label: `Search ${effectiveSites().length} sites for “${query}”`,
          detail: "Enter",
        });
      }
      for (const entry of recentQueries().slice(0, 4)) {
        if (lower && !entry.toLowerCase().includes(lower)) continue;
        items.push({ id: `recent-${entry}`, kind: "recent", label: entry, detail: "Recent" });
      }
      if (lower.length >= 2) {
        for (const provider of allSites
          .filter((entry) => entry.name.toLowerCase().includes(lower) || entry.id.includes(lower))
          .slice(0, 5)) {
          items.push({
            id: `site-${provider.id}`,
            kind: "site",
            label: provider.name,
            detail: "Site",
            value: provider.id,
          });
        }
        for (const id of categoryIds
          .filter((entry) => categoryTitle(entry).toLowerCase().includes(lower))
          .slice(0, 3)) {
          items.push({
            id: `cat-${id}`,
            kind: "category",
            label: categoryTitle(id),
            detail: "Category",
            value: id,
          });
        }
      }
      return items;
    },
    onPick: (item) => {
      const value = field.input.value.trim();
      if (item.kind === "search") {
        startSearchFor(value);
        return;
      }
      if (item.kind === "recent") {
        field.input.value = item.label;
        startSearchFor(item.label);
        return;
      }
      if (item.kind === "site" && item.value) {
        selectedSites.clear();
        selectedSites.add(item.value);
        paintSiteChips();
        paintScope();
        if (value.length < MIN_QUERY) {
          field.input.focus();
          hint.textContent = "Type what to look for, then press Enter.";
          return;
        }
        startSearchFor(value);
        return;
      }
      if (item.kind === "category" && item.value) {
        setSearchCategories([item.value]);
        paintScope();
        if (value.length < MIN_QUERY) {
          field.input.focus();
          hint.textContent = `Now type what to look for in ${categoryTitle(item.value)}, then press Enter.`;
          return;
        }
        startSearchFor(value);
        return;
      }
      if (item.kind === "browse") {
        context.navigate("#/providers");
      }
    },
    onSubmit: () => startSearchFor(field.input.value),
  });

  const recent = el("div", { class: "recent-queries", attrs: { "aria-label": "Recent searches" } });
  paintRecent = (): void => {
    const entries = recentQueries();
    recent.replaceChildren();
    if (entries.length === 0 || input.value.trim().length > 0) {
      recent.hidden = true;
      return;
    }
    recent.hidden = false;
    recent.append(el("span", { class: "recent-label", text: "Recent" }));
    for (const entry of entries) {
      const chip = el("button", { class: "tag tag--quiet", attrs: { type: "button" }, text: entry });
      chip.addEventListener("click", () => {
        input.value = entry;
        inputClear.hidden = false;
        window.clearTimeout(debounce);
        startSearchFor(entry);
      });
      recent.append(chip);
    }
    const clear = el("button", {
      class: "ghost-link",
      attrs: { type: "button" },
      text: "Clear",
    });
    clear.addEventListener("click", () => {
      clearRecentQueries();
      paintRecent();
    });
    recent.append(clear);
  };

  // ---- mount ----------------------------------------------------------

  root.append(
    el(
      "header",
      { class: "search-head container" },
      el("h1", { class: "page-title", text: "Search" }),
      field.form,
      hint,
      el("p", {
        class: "page-sub",
        text: "Every site at once, or narrow it to a category group. Results are kept when you open a title.",
      }),
      scopeLine,
      scopePills,
      recent,
      scopePanel,
    ),
    el(
      "div",
      { class: "container" },
      toolbar,
      stateBlock,
      el("div", { class: "search-progress-row" }, progress),
      kept,
      announcer.node,
      results,
    ),
  );

  const onScroll = (): void => {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      if (activeRun) setRunScroll(activeRun, window.scrollY);
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });

  const cleanup = (): void => {
    unsubscribe();
    window.removeEventListener("scroll", onScroll);
    window.clearTimeout(debounce);
    if (scrollRaf) cancelAnimationFrame(scrollRaf);
    announcer.stop();
  };
  window.addEventListener("hashchange", cleanup, { once: true });
  window.addEventListener("pagehide", cleanup, { once: true });

  const wanted = initialQuery.trim();
  const previous = wanted.length >= MIN_QUERY ? null : lastSearch();

  // Sites are part of the remembered scope. Restore them before resolving ids
  // so a saved run with a site filter can match.
  if (previous && initialSites.length === 0) {
    for (const id of previous.sites) if (validSites.has(id)) selectedSites.add(id);
  }
  pruneSites();

  paintScope();
  paintSiteChips();
  paintRecent();

  if (wanted.length >= MIN_QUERY) {
    input.value = wanted;
    inputClear.hidden = false;
    startSearchFor(wanted, true);
  } else {
    const ids = effectiveSites().map((site) => site.id);
    if (
      previous &&
      previous.query.length >= MIN_QUERY &&
      previous.ids.length === ids.length &&
      previous.ids.every((id) => ids.includes(id))
    ) {
      input.value = previous.query;
      inputClear.hidden = false;
      syncUrl(previous.query);
      attachRun(previous, true, true);
    } else {
      showIdle();
    }
  }

  requestAnimationFrame(() => input.focus({ preventScroll: true }));
  return root;
}

/**
 * Search control for a single provider. It sits as a compact toolbar above the
 * provider's own listing, so browsing stays one screen. The last query and its
 * results are kept per provider, so returning from a title preserves the panel.
 */
export function providerSearchView(context: AppContext, providerId: string): HTMLElement {
  const provider = context.providers.find((entry) => entry.id === providerId);
  const name = provider?.name ?? providerId;
  const root = el("section", { class: "provider-search" });

  const status = el("p", {
    class: "search-status-line",
    attrs: { id: "provider-search-status", role: "status", "aria-live": "polite", "aria-atomic": "true" },
  });
  const results = el("ul", {
    class: "grid grid--dense",
    attrs: { "aria-label": `Results on ${name}`, "aria-busy": "false" },
  });
  const actions = el("div", { class: "provider-search-actions" });

  let token = 0;
  let debounce = 0;
  let active: ProviderSearchEntry | null = null;
  let scrollRaf = 0;

  const openResult = (video: VideoSummary): void => {
    setProviderSearchScroll(providerId, window.scrollY);
    context.navigate(`#/watch/${encodeURIComponent(video.provider)}/${encodeURIComponent(video.id)}`);
  };

  const paint = (items: VideoSummary[], query: string, kept: boolean): void => {
    results.replaceChildren();
    results.removeAttribute("aria-busy");
    actions.replaceChildren();
    if (kept) {
      const refresh = el("button", {
        class: "btn btn-ghost btn-sm",
        attrs: { type: "button" },
        text: "Refresh",
      });
      refresh.addEventListener("click", () => void run(true));
      actions.append(refresh);
    }
    if (items.length === 0) {
      results.append(
        el(
          "li",
          { class: "grid-empty" },
          el("p", { class: "empty-body", text: `Nothing matched “${query}” on ${name}.` }),
        ),
      );
      return;
    }
    for (const item of items) results.append(searchPoster(item, name, query, openResult));
  };

  const searched = (query: string, items: VideoSummary[]): number => {
    const searchable = provider?.capabilities.search !== false;
    return searchable
      ? items.length
      : items.filter((item) => (item.title ?? "").toLowerCase().includes(query.toLowerCase())).length;
  };

  const paintStatus = (query: string, count: number, kept: boolean): void => {
    const searchable = provider?.capabilities.search !== false;
    const suffix = searchable ? "" : " This provider has no search endpoint.";
    const keptText = kept ? " Kept from your last search." : "";
    status.textContent =
      count === 0
        ? `No results for “${query}” on ${name}.${suffix}${keptText}`
        : `${plural(count, "result")} for “${query}” on ${name}.${suffix}${keptText}`;
  };

  const run = async (force: boolean): Promise<void> => {
    const query = input.value.trim();
    if (query.length < MIN_QUERY) {
      active = null;
      token += 1;
      results.replaceChildren();
      actions.replaceChildren();
      results.removeAttribute("aria-busy");
      delete status.dataset.loading;
      status.textContent = "Type at least two characters to search this provider.";
      return;
    }

    const cached = lastProviderSearch(providerId);
    if (!force && cached && cached.query.toLowerCase() === query.toLowerCase()) {
      active = cached;
      token += 1;
      delete status.dataset.loading;
      const count = searched(query, cached.items);
      paintStatus(query, count, true);
      paint(cached.items, query, true);
      if (cached.scrollY > 0) {
        requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: cached.scrollY })));
      }
      return;
    }

    const current = ++token;
    status.dataset.loading = "true";
    status.textContent = `Searching ${name}…`;
    results.setAttribute("aria-busy", "true");
    actions.replaceChildren();
    results.replaceChildren(
      ...Array.from({ length: 6 }, () => el("li", { class: "poster-cell poster--skeleton" }, el("span", { class: "poster-art" }))),
    );

    try {
      const fetched = await searchProvider(providerId, query, PAGE_SIZE);
      if (current !== token) return;
      // Some providers advertise search and answer every query with their
      // newest page. If not one returned title contains the word, the panel
      // says so instead of dressing that page up as results.
      const searchable = provider?.capabilities.search !== false;
      const needle = query.toLowerCase();
      const hits = fetched.filter((item) => (item.title ?? "").toLowerCase().includes(needle));
      const ignored = searchable && fetched.length > 0 && hits.length === 0;
      const items = searchable && !ignored ? fetched : hits;
      active = rememberProviderSearch(providerId, query, items);
      delete status.dataset.loading;
      paintStatus(query, items.length, false);
      if (ignored) {
        status.textContent = `No title matches “${query}” on ${name}. This provider returns its newest page for any search.`;
      }
      paint(items, query, false);
    } catch (error) {
      if (current !== token) return;
      const raw = error instanceof Error ? error.message : "Search failed";
      const message = /failed to fetch|networkerror|load failed/i.test(raw)
        ? "The provider did not answer. Check the connection and try again."
        : raw;
      results.replaceChildren();
      results.removeAttribute("aria-busy");
      delete status.dataset.loading;
      status.textContent = `Search failed on ${name}.`;
      actions.replaceChildren();
      const retry = el("button", {
        class: "btn btn-ghost btn-sm",
        attrs: { type: "button" },
        text: "Try again",
      });
      retry.addEventListener("click", () => void run(true));
      results.append(
        el(
          "li",
          { class: "grid-empty" },
          el(
            "div",
            { class: "empty" },
            el("p", { class: "empty-title", text: message }),
            retry,
          ),
        ),
      );
    }
  };

  const field = searchField({
    placeholder: `Search ${name}`,
    label: `Search ${name}`,
    describedBy: "provider-search-status",
    onInput: () => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(() => void run(false), DEBOUNCE_MS);
    },
    onSubmit: () => {
      window.clearTimeout(debounce);
      void run(false);
    },
  });
  const input = field.input;

  const openSite = provider?.siteUrl
    ? el("a", {
        class: "ghost-link",
        attrs: { href: provider.siteUrl, target: "_blank", rel: "noopener noreferrer" },
        text: "Open site",
      })
    : null;

  root.append(
    el("div", { class: "provider-search-row" }, field.form, openSite),
    status,
    actions,
    results,
  );

  const previous = lastProviderSearch(providerId);
  if (previous) {
    active = previous;
    input.value = previous.query;
    field.clear.hidden = false;
    paintStatus(previous.query, searched(previous.query, previous.items), true);
    paint(previous.items, previous.query, true);
    if (previous.scrollY > 0) {
      requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: previous.scrollY })));
    }
  } else {
    status.textContent = "Type at least two characters to search this provider.";
  }

  const onScroll = (): void => {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      if (active) setProviderSearchScroll(providerId, window.scrollY);
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });

  const cleanup = (): void => {
    window.removeEventListener("scroll", onScroll);
    window.clearTimeout(debounce);
    if (scrollRaf) cancelAnimationFrame(scrollRaf);
  };
  window.addEventListener("hashchange", cleanup, { once: true });
  window.addEventListener("pagehide", cleanup, { once: true });

  return root;
}
